import React from "react";
import { styled } from "styled-components";
import { Language } from "./types";

interface Props {
  currentLang: string;
  langs: Language[];
  setLang: (lang: Language) => void;
}

// Pill-style segmented toggle (e.g. EN / 中). Replaces the globe-icon dropdown, which read
// as a network switcher. Uses theme tokens so it matches the MacSwap palette.
const Track = styled.div`
  display: inline-flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.input};
  border-radius: 9999px;
  padding: 2px;
  flex-shrink: 0;
`;

const Segment = styled.button<{ $active: boolean }>`
  border: none;
  cursor: pointer;
  background: ${({ theme, $active }) => ($active ? theme.colors.backgroundAlt : "transparent")};
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.textSubtle)};
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 6px 10px;
  border-radius: 9999px;
  transition: color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
  box-shadow: ${({ $active }) => ($active ? "0 2px 6px rgba(0, 0, 0, 0.12)" : "none")};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

// "en" -> "EN", "zh-cn" -> "中"
const shortLabel = (lang: Language) =>
  lang.code.toLowerCase().startsWith("zh") ? "中" : lang.code.slice(0, 2).toUpperCase();

const LanguageToggle: React.FC<React.PropsWithChildren<Props>> = ({ currentLang, langs, setLang }) => (
  <Track role="group" aria-label="Language">
    {langs.map((lang) => {
      const active = currentLang === lang.code;
      return (
        <Segment
          key={lang.locale}
          type="button"
          $active={active}
          aria-pressed={active}
          onClick={() => !active && setLang(lang)}
        >
          {shortLabel(lang)}
        </Segment>
      );
    })}
  </Track>
);

export default React.memo(
  LanguageToggle,
  (prev, next) => prev.currentLang === next.currentLang && prev.langs === next.langs
);
